import styles from './Reports.module.css';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';

import React from 'react';

import { DateTime } from 'luxon';

import * as api from '../../api.js';
import { useAuth } from '../../AuthContext';

export const Reports = () => {
    const [ loading, setLoading ] = React.useState(true);
    const [ error, setError ] = React.useState(null);
    const [ sales, setSales ] = React.useState([]);
    const [ items, setItems ] = React.useState([]);
    const [ users, setUsers ] = React.useState([]);
    const [ tabValue, setTabValue ] = React.useState(0);
    const [ itemsSort, setItemsSort ] = React.useState({ field: 'name', order: 'asc' });
    const [ sellersSort, setSellersSort ] = React.useState({ field: 'name', order: 'asc' });

    const { token } = useAuth();

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const [ salesData, itemsData, usersData ] = await Promise.all([
                    api.getAllSales(token),
                    api.getAllItems(token),
                    api.getAllUsers(token)
                ]);

                if (salesData.error) {
                    setError(salesData.error);
                } else {
                    setSales(salesData);
                }

                if (itemsData.error) {
                    setError(itemsData.error);
                } else {
                    setItems(itemsData);
                }

                if (usersData.error) {
                    setError(usersData.error);
                } else {
                    setUsers(usersData);
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [ token ]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleItemsSort = (field) => {
        setItemsSort(prev => ({
            field,
            order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
        }));
    };

    const handleSellersSort = (field) => {
        setSellersSort(prev => ({
            field,
            order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
        }));
    };

    const salesReport = () => {
        // Get current year for filtering
        const currentYear = DateTime.now().year;
        const startOfYear = DateTime.local(currentYear, 1, 1).toMillis();
        const endOfYear = DateTime.local(currentYear + 1, 1, 1).toMillis();

        // Filter sales and items to current year
        const currentYearSales = sales.filter(sale => {
            const saleDate = DateTime.fromISO(sale.createdAt || 0).toMillis();

            return saleDate >= startOfYear && saleDate < endOfYear;
        });

        const currentYearItems = items.filter(item => {
            const itemDate = item.createdAt || 0;

            return itemDate >= startOfYear && itemDate < endOfYear;
        });

        // Calculate totals by payment method
        const paymentTotals = {
            cash: { count: 0, amount: 0 },
            card: { count: 0, amount: 0 },
            check: { count: 0, amount: 0 },
            total: { count: 0, amount: 0 }
        };

        currentYearSales.forEach(sale => {
            const method = (sale.paymentMethod || 'cash').toLowerCase();
            const amount = sale.total || sale.amount || 0;

            if (paymentTotals[ method ]) {
                paymentTotals[ method ].count += 1;
                paymentTotals[ method ].amount += amount;

                paymentTotals.total.count += 1;
                paymentTotals.total.amount += amount;
            }
        });

        // Calculate additional totals from items
        let soldNotPaid = 0;
        let soldNotPaidCount = 0;
        let totalPaidOut = 0;
        let totalPaidOutCount = 0;
        let totalDonated = 0;
        let totalDonatedCount = 0;  
        let totalSoldAmount = 0;
        let totalSoldCount = 0;

        // Group items by vendor
        const vendorMap = {};

        currentYearItems.forEach(item => {
            const seller = users.find(u => u.id === item.seller);
            const vendorName = seller?.vendorName || (seller ? `${ seller.firstName || '' } ${ seller.lastName || '' }`.trim() || seller.email : 'Unknown');

            if (!vendorMap[ vendorName ]) {
                vendorMap[ vendorName ] = {
                    itemsListed: 0,
                    itemsSold: 0,
                    totalValue: 0,
                    totalSoldValue: 0,
                    totalDonation: 0,
                    totalPaidOut: 0,
                    soldNotPaid: 0
                };
            }

            vendorMap[ vendorName ].itemsListed += 1;
            vendorMap[ vendorName ].totalValue += item.price || 0;
            vendorMap[ vendorName ].totalDonation += item.donation || 0;

            if (item.sold) {
                vendorMap[ vendorName ].itemsSold += 1;
                vendorMap[ vendorName ].totalSoldValue += item.price || 0;

                totalSoldAmount += item.price || 0;
                totalSoldCount += 1;

                if (item.payoutAmount) {
                    vendorMap[ vendorName ].totalPaidOut += item.payoutAmount;
                } else if (!item.paidOut) {
                    vendorMap[ vendorName ].soldNotPaid += item.price || 0;
                }

                if (!item.payoutAmount && !item.paidOut) {
                    soldNotPaid += item.price || 0;
                    soldNotPaidCount += 1;
                }

                if (item.payoutAmount) {
                    totalPaidOut += item.payoutAmount;
                    totalPaidOutCount += 1;
                }
            }

            if (item.donated) {
                totalDonated += item.price || 0;
                totalDonatedCount += 1;
            }
        });

        const vendors = Object.entries(vendorMap).map(([ name, data ]) => {
            return { name, ...data };
        }).sort((a, b) => b.totalValue - a.totalValue);

        return (
            <div>
                <TableContainer component={ Paper } sx={ { mb: 2 } }>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Payment Method</TableCell>
                                <TableCell>Count</TableCell>
                                <TableCell>Amount</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            <TableRow>
                                <TableCell>Cash</TableCell>
                                <TableCell>{ paymentTotals.cash.count }</TableCell>
                                <TableCell>${ paymentTotals.cash.amount.toFixed(2) }</TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell>Credit</TableCell>
                                <TableCell>{ paymentTotals.card.count }</TableCell>
                                <TableCell>${ paymentTotals.card.amount.toFixed(2) }</TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell>Check</TableCell>
                                <TableCell>{ paymentTotals.check.count }</TableCell>
                                <TableCell>${ paymentTotals.check.amount.toFixed(2) }</TableCell>
                            </TableRow>

                            <TableRow sx={ { fontWeight: 'bold' } }>
                                <TableCell>Total Sales</TableCell>
                                <TableCell>{ paymentTotals.total.count }</TableCell>
                                <TableCell>${ paymentTotals.total.amount.toFixed(2) }</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <TableContainer component={ Paper } sx={ { mb: 2 } }>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Summary</TableCell>
                                <TableCell>Count</TableCell>
                                <TableCell>Amount</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            <TableRow>
                                <TableCell>Sold but Not Paid</TableCell>
                                <TableCell>{ soldNotPaidCount }</TableCell>
                                <TableCell>${ soldNotPaid.toFixed(2) }</TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell>Total Paid Out</TableCell>
                                <TableCell>{ totalPaidOutCount }</TableCell>
                                <TableCell>${ totalPaidOut.toFixed(2) }</TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell>Total Donated</TableCell>
                                <TableCell>{ totalDonatedCount }</TableCell>
                                <TableCell>${ totalDonated.toFixed(2) }</TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell>Total Profit</TableCell>
                                <TableCell>{ totalSoldCount }</TableCell>
                                <TableCell>${ (totalSoldAmount * 0.1).toFixed(2) }</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <TableContainer component={ Paper }>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Vendor</TableCell>
                                <TableCell>Items Listed</TableCell>
                                <TableCell>Items Sold</TableCell>
                                <TableCell>Total Value</TableCell>
                                <TableCell>Total Donation</TableCell>
                                <TableCell>Total Paid Out</TableCell>
                                <TableCell>Sold Not Paid</TableCell>
                                <TableCell>Total Sold</TableCell>
                                <TableCell>Profit</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            { vendors.map((vendor) => (
                                <TableRow key={ vendor.name }>
                                    <TableCell>{ vendor.name }</TableCell>
                                    <TableCell>{ vendor.itemsListed }</TableCell>
                                    <TableCell>{ vendor.itemsSold }</TableCell>
                                    <TableCell>${ vendor.totalValue.toFixed(2) }</TableCell>
                                    <TableCell>${ vendor.totalDonation.toFixed(2) }</TableCell>
                                    <TableCell>${ vendor.totalPaidOut.toFixed(2) }</TableCell>
                                    <TableCell>${ vendor.soldNotPaid.toFixed(2) }</TableCell>
                                    <TableCell>${ vendor.totalSoldValue.toFixed(2) }</TableCell>
                                    <TableCell>${ (vendor.totalSoldValue * (vendor.vendorFee || 0.1)).toFixed(2) }</TableCell>
                                </TableRow>
                            )) }
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        );
    };

    const itemsReport = () => {
        // Get current year for filtering
        const currentYear = new Date().getFullYear();
        const startOfYear = new Date(currentYear, 0, 1).getTime();
        const endOfYear = new Date(currentYear + 1, 0, 1).getTime();

        // Filter items to current year
        const currentYearItems = items.filter(item => {
            const itemDate = item.createdAt || 0;

            return itemDate >= startOfYear && itemDate < endOfYear;
        });

        const sortedItems = [ ...currentYearItems ].sort((a, b) => {
            let aValue, bValue;

            switch (itemsSort.field) {
                case 'name':
                    aValue = (a.name || '').toLowerCase();
                    bValue = (b.name || '').toLowerCase();

                    break;
                case 'price':
                    aValue = a.price || 0;
                    bValue = b.price || 0;

                    break;
                case 'donation':
                    aValue = a.donation || 0;
                    bValue = b.donation || 0;

                    break;
                case 'seller': {
                    const aSeller = users.find(u => u.id === a.seller);
                    const bSeller = users.find(u => u.id === b.seller);

                    aValue = (aSeller ? aSeller.email || a.seller : a.seller).toLowerCase();
                    bValue = (bSeller ? bSeller.email || b.seller : b.seller).toLowerCase();

                    break;
                }
                default:
                    aValue = a[ itemsSort.field ] || '';
                    bValue = b[ itemsSort.field ] || '';
            }

            if (aValue < bValue) return itemsSort.order === 'asc' ? -1 : 1;
            if (aValue > bValue) return itemsSort.order === 'asc' ? 1 : -1;

            return 0;
        });

        const SortableTableCell = ({ field, children }) => (
            <TableCell
                onClick={ () => handleItemsSort(field) }
                sx={ {
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                } }
            >
                { children } { itemsSort.field === field ? (itemsSort.order === 'asc' ? '↑' : '↓') : '' }
            </TableCell>
        );

        return (
            <TableContainer component={ Paper }>
                <Table>
                    <TableHead>
                        <TableRow>
                            <SortableTableCell field="name">Item Name</SortableTableCell>
                            <TableCell>Description</TableCell>
                            <SortableTableCell field="price">Price</SortableTableCell>
                            <SortableTableCell field="donation">Donation</SortableTableCell>
                            <TableCell>Fee Paid</TableCell>
                            <TableCell>Verified</TableCell>
                            <TableCell>Sold</TableCell>
                            <SortableTableCell field="seller">Seller</SortableTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        { sortedItems.map((item) => {
                            const seller = users.find(u => u.id === item.seller);
                            return (
                                <TableRow key={ item.id }>
                                    <TableCell>{ item.name || 'N/A' }</TableCell>
                                    <TableCell>{ item.description || 'N/A' }</TableCell>
                                    <TableCell>${ (item.price || 0).toFixed(2) }</TableCell>
                                    <TableCell>{ item.donated ? 'Yes' : 'No' }</TableCell>
                                    <TableCell>{ item.feePaid ? 'Yes' : 'No' }</TableCell>
                                    <TableCell>{ item.verified ? 'Yes' : 'No' }</TableCell>
                                    <TableCell>{ item.sold ? 'Yes' : 'No' }</TableCell>
                                    <TableCell>{ seller ? seller.email || seller.email || item.seller : item.seller }</TableCell>
                                </TableRow>
                            );
                        }) }
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    const sellersReport = () => {
        // Get current year for filtering
        const currentYear = new Date().getFullYear();
        const startOfYear = new Date(currentYear, 0, 1).getTime();
        const endOfYear = new Date(currentYear + 1, 0, 1).getTime();

        // Filter items to current year
        const currentYearItems = items.filter(item => {
            const itemDate = item.createdAt || 0;

            return itemDate >= startOfYear && itemDate < endOfYear;
        });

        // Group items by seller
        const sellerMap = {};

        currentYearItems.forEach(item => {
            if (!sellerMap[ item.seller ]) {
                const seller = users.find(u => u.id === item.seller);
                sellerMap[ item.seller ] = {
                    name: seller ? `${ seller.firstName || '' } ${ seller.lastName || '' }`.trim() || seller.email || item.seller : item.seller,
                    items: [],
                    totalPayout: 0,
                    totalDonation: 0,
                    feesPaid: 0,
                    itemsSold: 0
                };
            }
            sellerMap[ item.seller ].items.push(item);
            sellerMap[ item.seller ].totalPayout += item.price || 0;
            sellerMap[ item.seller ].totalDonation += item.donation || 0;

            if (item.feePaid) {
                sellerMap[ item.seller ].feesPaid += 1;
            }
            if (item.sold) {
                sellerMap[ item.seller ].itemsSold += 1;
            }
        });

        const sellers = Object.values(sellerMap).sort((a, b) => {
            let aValue, bValue;

            switch (sellersSort.field) {
                case 'name':
                    aValue = a.name.toLowerCase();
                    bValue = b.name.toLowerCase();

                    break;
                case 'itemsListed':
                    aValue = a.items.length;
                    bValue = b.items.length;

                    break;
                case 'itemsSold':
                    aValue = a.itemsSold;
                    bValue = b.itemsSold;

                    break;
                case 'totalPayout':
                    aValue = a.totalPayout;
                    bValue = b.totalPayout;

                    break;
                case 'totalDonation':
                    aValue = a.totalDonation;
                    bValue = b.totalDonation;

                    break;
                case 'feesPaid':
                    aValue = a.feesPaid;
                    bValue = b.feesPaid;

                    break;
                default:
                    aValue = a[ sellersSort.field ] || '';
                    bValue = b[ sellersSort.field ] || '';
            }

            if (aValue < bValue) return sellersSort.order === 'asc' ? -1 : 1;
            if (aValue > bValue) return sellersSort.order === 'asc' ? 1 : -1;

            return 0;
        });

        const SortableTableCell = ({ field, children }) => (
            <TableCell
                onClick={ () => handleSellersSort(field) }
                sx={ {
                    cursor: 'pointer',
                    userSelect: 'none',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                } }
            >
                { children } { sellersSort.field === field ? (sellersSort.order === 'asc' ? '↑' : '↓') : '' }
            </TableCell>
        );

        return (
            <TableContainer component={ Paper }>
                <Table>
                    <TableHead>
                        <TableRow>
                            <SortableTableCell field="name">Seller Name</SortableTableCell>
                            <SortableTableCell field="itemsListed">Items Listed</SortableTableCell>
                            <SortableTableCell field="itemsSold">Items Sold</SortableTableCell>
                            <SortableTableCell field="totalPayout">Total Payout</SortableTableCell>
                            <SortableTableCell field="totalDonation">Total Donation</SortableTableCell>
                            <SortableTableCell field="feesPaid">Fees Paid</SortableTableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        { sellers.map((seller) => (
                            <TableRow key={ seller.name }>
                                <TableCell>{ seller.name }</TableCell>
                                <TableCell>{ seller.items.length }</TableCell>
                                <TableCell>{ seller.itemsSold }</TableCell>
                                <TableCell>${ seller.totalPayout.toFixed(2) }</TableCell>
                                <TableCell>${ seller.totalDonation.toFixed(2) }</TableCell>
                                <TableCell>{ seller.feesPaid }</TableCell>
                            </TableRow>
                        )) }
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    if (loading) {
        return <CircularProgress />;
    }

    if (error) {
        return <Alert variant='filled' severity="error" onClose={ () => setError(false) }>{ error }</Alert>;
    }

    return (
        <div className={ styles.reports }>
            <Typography variant="h4" gutterBottom>
                Reports
            </Typography>

            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Showing data for { new Date().getFullYear() } only
            </Typography>

            <Box sx={ { width: '100%', mt: 2 } }>
                <Tabs value={ tabValue } onChange={ handleTabChange }>
                    <Tab label="Sales Report" />
                    <Tab label="Items Report" />
                    <Tab label="Sellers Report" />
                </Tabs>
                
                <Box sx={ { mt: 2 } }>
                    { tabValue === 0 && salesReport() }
                    { tabValue === 1 && itemsReport() }
                    { tabValue === 2 && sellersReport() }
                </Box>
            </Box>
        </div>
    );
};
